import React from 'react';
import cn from 'bem-cn';
import { useNavigate } from 'react-router-dom';
import { PromptCardRedesign } from '@ai-platform/utils';

import { useExperimentFlag } from '../../contexts';
import { useParallax } from '../PromptCard/useParallax';
import { ScreenSize, useGetScreenSize } from '../../hooks';
import { Button, Link } from '../UI';
import { removeHangingPrepositions } from '../../utils/text/removeHangingPrepositions';

import { CourseCardProps } from './CourseCard.types';

import './CourseCard.scss';

const b = cn('course-card');

export const CourseCard: React.FC<CourseCardProps> = ({ course, withHover = true, className }) => {
    const experimentFlag = useExperimentFlag('promptCardRedesign');
    const screenSize = useGetScreenSize();
    const navigate = useNavigate();
    const { panelRef, transformX, transformY } = useParallax(0.3);

    // Функция для склонения слова "глава"
    const getChapterWord = (count: number): string => {
        if (count === 1) {
            return 'глава';
        }
        if (count < 5) {
            return 'главы';
        }

        return 'глав';
    };

    const handleStartCourse = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();

        const targetUrl = course.externalLink || `/course/${course.id}`;

        if (course.externalLink) {
            window.open(targetUrl, '_blank', 'noopener,noreferrer');
        } else {
            navigate(targetUrl);
        }
    };
    const isMobile = screenSize === ScreenSize.Mobile;
    const isNewDesignCard = isMobile && experimentFlag === PromptCardRedesign.New;

    const needHover = !isMobile && screenSize !== ScreenSize.Tablet && withHover;

    // Новая структура слоев параллакса - только параллакс трансформации
    const cardParallaxStyle = needHover
        ? {
            transform: `perspective(200px) rotateY(${transformX}deg) rotateX(${transformY}deg)`,
        }
        : {};

    const backgroundStyle = {
        '--bg-image': `url(${course.image})`,
    } as React.CSSProperties;

    return (
        <Link
            to={course.externalLink || `/course/${course.id}`}
            external={Boolean(course.externalLink)}
            ref={panelRef}
            className={b({ 'with-hover': withHover, experiment: isNewDesignCard }, [className])}
        >
            {needHover && <div className={b('hover-trigger')} />}
            <div style={cardParallaxStyle} className={b('parallax')}>
                <div className={b('background')} style={backgroundStyle} />
                <div className={b('content')}>
                    <div className={b('header')}>
                        <div>Курс</div>
                    </div>
                    <div className={b('meta')}>
                        {course.timeToComplete && <div className={b('meta-item')}>{course.timeToComplete}</div>}
                        {course.numberOfChapters && (
                            <div className={b('meta-item')}>
                                {course.numberOfChapters} {getChapterWord(course.numberOfChapters)}
                            </div>
                        )}
                    </div>
                    <Button className={b('start-button')} onClick={handleStartCourse}>
                        Пройти курс
                    </Button>
                    <h2 className={b('title')}>{course.title}</h2>
                    <p className={b('subtitle')}>{removeHangingPrepositions(course.tagline)}</p>
                </div>
            </div>
        </Link>
    );
};
